import { ClassConstructor } from "class-transformer";
import { Method } from "axios";
import { ReadInspectionListReqDto, ReadInspectionListResDto } from "@/dto/ReadInpectionListDto.ts";
import { EmptyDto, Id } from "@/dto/CommonDto.ts";
import { ReadCpoListResDto } from "@/dto/ReadCpoListDto.ts";
import { ReadMakerListResDto } from "@/dto/ReadMakerListDto.ts";
import { ReadStationDetailReqDto, ReadStationDetailResDto } from "@/dto/ReadStationDetailDto.ts";
import { ReadStationListResDto } from "@/dto/ReadStationListDto.ts";
import { SlowInspectionDto } from "@/dto/inspection/slow/SlowInspectionDto.ts";
import { RapidInspectionDto } from "@/dto/inspection/rapid/RapidInspectionDto.ts";
import { SlowInspectionReqDto } from "@/dto/inspection/slow/SlowInspectionReqDto.ts";
import { RapidInspectionReqDto } from "@/dto/inspection/rapid/RapidInspectionReqDto.ts";
import { DeleteInspectionReqDto } from "@/dto/inspection/DeleteInspectionDto.ts";
import { ReadInspectorListResDto } from "@/dto/inspector/ReadInspectorListDto.ts";
import { DeleteInspectorReqDto } from "@/dto/inspector/DeleteInspectorDto.ts";
import { ReadInspectorDetailReqDto, ReadInspectorDetailResDto } from "@/dto/inspector/ReadInspectorDetailDto";
import { InspectorReqDto } from "@/dto/inspector/InspectorReqDto.ts";
import { InspectorAdminReqDto } from "@/dto/inspector/InspectorAdminReqDto.ts";
import { CreateDocumentResDto, CreateDocumentWithFilterReqDto, CreateDocumentWithIdsReqDto, CreateKecoExcelReqDto } from "@/dto/inspection/CreateDocumentDto.ts";
import { ReadBackendVersionResDto } from "@/dto/ReadBackendVersionDto.ts";

export const VERSION_TAG: string = "/api/v1";

export const enum EndpointEnum {
    /**
     * 점검 리스트 조회
     */
    READ_INSPECTION_LIST = "READ_INSPECTION_LIST",
    /**
     * 점검 목록 엑셀 다운로드
     * */
    READ_INSPECTION_LIST_EXCEL = "READ_INSPECTION_LIST_EXCEL",
    /**
     * CPO 리스트 조회
     */
    READ_CPO_LIST = "READ_CPO_LIST",

    /**
     * 제조가 리스트 조회
     */
    READ_MAKER_LIST = "READ_MAKER_LIST",
    /**
     * 충전소 리스트 조회
     */
    READ_STATION_LIST = "READ_STATION_LIST",
    /**
     * 충전소 상세 조회
     */
    READ_STATION_DETAIL = "READ_STATION_DETAIL",

    READ_RAPID_STATION_INSPECTION = "READ_RAPID_STATION_INSPECTION",
    CREATE_RAPID_STATION_INSPECTION = "CREATE_RAPID_STATION_INSPECTION",
    UPDATE_RAPID_STATION_INSPECTION = "UPDATE_RAPID_STATION_INSPECTION",

    READ_SLOW_STATION_INSPECTION = "READ_SLOW_STATION_INSPECTION",
    CREATE_SLOW_STATION_INSPECTION = "CREATE_SLOW_STATION_INSPECTION",
    UPDATE_SLOW_STATION_INSPECTION = "UPDATE_SLOW_STATION_INSPECTION",

    DELETE_INSPECTION = "DELETE_INSPECTION",
    /**
     * 점검 사용자 리스트 조회
     * */
    READ_INSPECTOR_LIST = "READ_INSPECTOR_LIST",
    /**
     * 점검 사용자 등록
     * */
    CREATE_INSPECTOR = "CREATE_INSPECTOR",
    /**
     * 점검 사용자 상세 조회
     * */
    READ_INSPECTOR_DETAIL = "READ_INSPECTOR_DETAIL",
    /**
     * 점검 사용자 수정(MASTER)
     * */
    UPDATE_INSPECTOR = "UPDATE_INSPECTOR",
    /**
     * 점검 사용자 수정(ADMIN)
     * */
    UPDATE_ADMIN_INSPECTOR = "UPDATE_ADMIN_INSPECTOR",
    /**
     * 점검 사용자 삭제
     * */
    DELETE_INSPECTOR = "DELETE_INSPECTOR",
    /**
     * 선택 보고서 변환
     * */
    CREATE_DOCUMENT_WITH_IDS = "CREATE_DOCUMENT_WITH_IDS",
    /**
     * 특정 사업 전체 보고서 변환
     * */
    CREATE_DOCUMENT_WITH_FILTER = "CREATE_DOCUMENT_WITH_FILTER",
    /**
     * 환경부 제출용 엑셀 다운로드
     * */
    CREATE_KECO_EXCEL = "CREATE_KECO_EXCEL",
    /**
     * 백엔드 버전
     * */
    READ_BE_VERSION = "READ_BE_VERSION",
}

export type QueryKeyType = (EndpointEnum | string)[];

type EndpointConfigType = {
    /**
     * method
     */
    method: Method;
    /**
     * response dto
     */
    responseDto?: ClassConstructor<any>;
    /**
     * request dto
     */
    requestDto: ClassConstructor<any>;
    /**
     * 요청 타입
     */
    contentType?: string;
    /**
     * 응답 타입
     */
    responseType?: string;
    /**
     * 주소
     */
    uri: string;
    /**
     * 해당 API 의 응답이 성공했을 때 Invalidate 시킬 query Key
     */
    queryKeyForInvalidateOnSuccess?: QueryKeyType;
    /**
     * 데이터 캐싱 & 식별
     * */
    queryKey?: (req: any) => QueryKeyType;
    completeMessage?: string;
    showLoading?: boolean;
};

export const EndpointConfig: Record<EndpointEnum, EndpointConfigType> = {
    READ_INSPECTION_LIST: {
        requestDto: ReadInspectionListReqDto,
        responseDto: ReadInspectionListResDto,
        uri: `${VERSION_TAG}/inspections`,
        method: "GET",
    },
    READ_INSPECTION_LIST_EXCEL: {
        requestDto: ReadInspectionListReqDto,
        responseDto: EmptyDto,
        uri: `${VERSION_TAG}/inspections/excel`,
        method: "GET",
        responseType: "blob",
        completeMessage: "엑셀 다운로드 완료",
        showLoading: true,
    },
    READ_CPO_LIST: {
        requestDto: EmptyDto,
        responseDto: ReadCpoListResDto,
        uri: `${VERSION_TAG}/common/cpos`,
        method: "GET",
    },
    READ_MAKER_LIST: {
        requestDto: EmptyDto,
        responseDto: ReadMakerListResDto,
        uri: `${VERSION_TAG}/common/makers`,
        method: "GET",
    },
    READ_STATION_LIST: {
        requestDto: EmptyDto,
        responseDto: ReadStationListResDto,
        uri: `${VERSION_TAG}/common/stations`,
        method: "GET",
    },
    READ_STATION_DETAIL: {
        requestDto: ReadStationDetailReqDto,
        responseDto: ReadStationDetailResDto,
        uri: `${VERSION_TAG}/common/stations/{id}`,
        method: "GET",
        showLoading: true,
    },

    READ_RAPID_STATION_INSPECTION: {
        requestDto: Id,
        responseDto: RapidInspectionReqDto,
        uri: `${VERSION_TAG}/inspections/rapid/{id}`,
        queryKey: (req: RapidInspectionDto) => [req.id],
        method: "GET",
        showLoading: true,
    },
    CREATE_RAPID_STATION_INSPECTION: {
        requestDto: RapidInspectionDto,
        responseDto: Id,
        uri: `${VERSION_TAG}/inspections/rapid`,
        method: "POST",
        contentType: "multipart/form-data",
        completeMessage: "생성 완료",
        showLoading: true,
    },
    UPDATE_RAPID_STATION_INSPECTION: {
        requestDto: RapidInspectionDto,
        responseDto: EmptyDto,
        uri: `${VERSION_TAG}/inspections/rapid/{id}`,
        method: "POST",
        contentType: "multipart/form-data",
        completeMessage: "저장 완료",
        showLoading: true,
        queryKeyForInvalidateOnSuccess: [EndpointEnum.READ_RAPID_STATION_INSPECTION],
    },
    READ_SLOW_STATION_INSPECTION: {
        requestDto: Id,
        responseDto: SlowInspectionReqDto,
        uri: `${VERSION_TAG}/inspections/slow/{id}`,
        queryKey: (req: SlowInspectionDto) => [req.id],
        method: "GET",
        showLoading: true,
    },
    CREATE_SLOW_STATION_INSPECTION: {
        requestDto: SlowInspectionDto,
        responseDto: Id,
        uri: `${VERSION_TAG}/inspections/slow`,
        method: "POST",
        contentType: "multipart/form-data",
        completeMessage: "생성 완료",
        showLoading: true,
    },
    UPDATE_SLOW_STATION_INSPECTION: {
        requestDto: SlowInspectionDto,
        responseDto: EmptyDto,
        uri: `${VERSION_TAG}/inspections/slow/{id}`,
        method: "POST",
        contentType: "multipart/form-data",
        completeMessage: "저장 완료",
        showLoading: true,
        queryKeyForInvalidateOnSuccess: [EndpointEnum.READ_SLOW_STATION_INSPECTION],
    },
    DELETE_INSPECTION: {
        requestDto: DeleteInspectionReqDto,
        responseDto: EmptyDto,
        uri: `${VERSION_TAG}/inspections`,
        method: "DELETE",
        completeMessage: "삭제 완료",
        showLoading: true,
        queryKeyForInvalidateOnSuccess: [EndpointEnum.READ_INSPECTION_LIST],
    },
    READ_INSPECTOR_LIST: {
        requestDto: EmptyDto,
        responseDto: ReadInspectorListResDto,
        uri: `${VERSION_TAG}/inspectors`,
        method: "GET",
    },
    CREATE_INSPECTOR: {
        requestDto: InspectorReqDto,
        responseDto: EmptyDto,
        uri: `${VERSION_TAG}/inspectors`,
        method: "POST",
        completeMessage: "점검 사용자 등록 완료",
        showLoading: true,
        queryKeyForInvalidateOnSuccess: [EndpointEnum.READ_INSPECTOR_LIST],
    },
    READ_INSPECTOR_DETAIL: {
        requestDto: ReadInspectorDetailReqDto,
        responseDto: ReadInspectorDetailResDto,
        uri: `${VERSION_TAG}/inspectors/detail/{uuid}`,
        method: "GET",
        queryKey: (req: ReadInspectorDetailReqDto) => [req.uuid],
        showLoading: true,
    },
    UPDATE_INSPECTOR: {
        requestDto: InspectorReqDto,
        responseDto: EmptyDto,
        uri: `${VERSION_TAG}/inspectors`,
        method: "PUT",
        completeMessage: "점검 사용자 수정 완료",
        showLoading: true,
        queryKeyForInvalidateOnSuccess: [EndpointEnum.READ_INSPECTOR_LIST],
    },
    UPDATE_ADMIN_INSPECTOR: {
        requestDto: InspectorAdminReqDto,
        responseDto: EmptyDto,
        uri: `${VERSION_TAG}/inspectors/admin`,
        method: "PUT",
        completeMessage: "점검 사용자 수정 완료",
        showLoading: true,
        queryKeyForInvalidateOnSuccess: [EndpointEnum.READ_INSPECTOR_LIST],
    },
    DELETE_INSPECTOR: {
        requestDto: DeleteInspectorReqDto,
        responseDto: EmptyDto,
        uri: `${VERSION_TAG}/inspectors/{uuid}`,
        method: "DELETE",
        completeMessage: "점검 사용자 삭제 완료",
        showLoading: true,
        queryKeyForInvalidateOnSuccess: [EndpointEnum.READ_INSPECTOR_LIST],
    },
    CREATE_DOCUMENT_WITH_IDS: {
        requestDto: CreateDocumentWithIdsReqDto,
        responseDto: CreateDocumentResDto,
        uri: `${VERSION_TAG}/inspections/report/download/select`,
        method: "POST",
        completeMessage: "보고서 변환 완료",
        showLoading: true,
    },
    CREATE_DOCUMENT_WITH_FILTER: {
        requestDto: CreateDocumentWithFilterReqDto,
        responseDto: CreateDocumentResDto,
        uri: `${VERSION_TAG}/inspections/report/download/all`,
        method: "POST",
        completeMessage: "보고서 변환 완료",
        showLoading: true,
    },
    CREATE_KECO_EXCEL: {
        requestDto: CreateKecoExcelReqDto,
        responseDto: CreateDocumentResDto,
        uri: `${VERSION_TAG}/inspections/excel/download/keco`,
        method: "POST",
        completeMessage: "엑셀 다운로드 완료",
        showLoading: true,
    },
    READ_BE_VERSION: {
        requestDto: EmptyDto,
        responseDto: ReadBackendVersionResDto,
        uri: `${VERSION_TAG}/version`,
        method: "GET",
    },
};